import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import ConversationsList from './ConversationList/ConversationsList'
import Messages from './Messages/Messages'
import ConversationDetails from './ConversationDetails/ConversationDetails'
import DeleteConversationModal from './Modals/DeleteConversationModal'
import SearchInput from './ControlBar/SearchInput'
import { useInboxStore } from '../../../../stores/inbox/useInboxStore'
import { isSalesNavigatorSessionReady } from '../../../../services/inbox/salesNavigatorAccess'

const MIN_LEFT_WIDTH = 290
const MAX_LEFT_ABS = 640
const DEFAULT_LEFT_WIDTH = 320
const CHAT_MIN_WIDTH = 360
const DETAILS_MAX_WIDTH = 360
const DETAILS_MIN_WIDTH = 280
const RESIZE_HANDLE_WIDTH = 6
const STORAGE_KEY = 'ld-inbox-left-panel-width'

function readStoredWidth(): number {
    try {
        const n = Number(localStorage.getItem(STORAGE_KEY))
        if (Number.isFinite(n)) return n
    } catch {
        /* ignore */
    }
    return DEFAULT_LEFT_WIDTH
}

const Inbox: React.FC = () => {
    const { store: { selectedConversation, conversationDetails, fetchingConversations, modal, selectedTab, salesNavigatorData } } = useInboxStore()
    const containerRef = useRef<HTMLDivElement>(null)
    const dragRef = useRef<{ startX: number; startW: number } | null>(null)
    const [showSidebar, setShowSidebar] = useState(false)
    const [leftPanelWidth, setLeftPanelWidth] = useState(readStoredWidth)

    const clampWidth = useCallback((w: number) => {
        const cw =
            containerRef.current?.clientWidth ??
            (typeof window !== 'undefined' ? window.innerWidth : 1200)
        const detailsWidth = showSidebar
            ? Math.min(DETAILS_MAX_WIDTH, Math.max(DETAILS_MIN_WIDTH, Math.floor(cw * 0.27)))
            : 0
        const maxWithChatReserve = cw - detailsWidth - CHAT_MIN_WIDTH - RESIZE_HANDLE_WIDTH
        const max = Math.max(MIN_LEFT_WIDTH, Math.min(MAX_LEFT_ABS, Math.floor(cw * 0.55), maxWithChatReserve))
        return Math.min(Math.max(MIN_LEFT_WIDTH, w), max)
    }, [showSidebar])

    useEffect(() => {
        setLeftPanelWidth((prev) => clampWidth(prev))
    }, [clampWidth])

    useEffect(() => {
        const el = containerRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const ro = new ResizeObserver(() => {
            setLeftPanelWidth((prev) => clampWidth(prev))
        })
        ro.observe(el)
        return () => ro.disconnect()
    }, [clampWidth])

    useEffect(() => {
        const isSingleParticipant = selectedConversation?.participants?.length === 1
        setShowSidebar(isSingleParticipant && conversationDetails)
    }, [selectedConversation, conversationDetails])

    const handleResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (e.button !== 0) return
        e.preventDefault()
        dragRef.current = { startX: e.clientX, startW: leftPanelWidth }
        e.currentTarget.setPointerCapture(e.pointerId)
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
    }

    const handleResizePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragRef.current) return
        const next = clampWidth(dragRef.current.startW + (e.clientX - dragRef.current.startX))
        setLeftPanelWidth(next)
    }

    const endResize = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragRef.current) return
        const next = clampWidth(dragRef.current.startW + (e.clientX - dragRef.current.startX))
        setLeftPanelWidth(next)
        dragRef.current = null
        try {
            localStorage.setItem(STORAGE_KEY, String(next))
        } catch {
            /* ignore */
        }
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId)
        }
    }

    const handleResizePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        endResize(e)
    }

    const handleResizePointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
        endResize(e)
    }

    return (
        <Container ref={containerRef}>
            <LeftPanel $width={leftPanelWidth}>
                <SearchWrapper>
                    <SearchInput
                        disabled={
                            fetchingConversations ||
                            (selectedTab === 'SalesNavigator' && !isSalesNavigatorSessionReady(salesNavigatorData))
                        }
                    />
                </SearchWrapper>
                <ConversationsList />
            </LeftPanel>
            <ResizeHandle
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize conversation list"
                onPointerDown={handleResizePointerDown}
                onPointerMove={handleResizePointerMove}
                onPointerUp={handleResizePointerUp}
                onPointerCancel={handleResizePointerCancel}
            />
            <MessagesPanel>
                <Messages showSidebar={showSidebar} />
            </MessagesPanel>
            {(showSidebar || (fetchingConversations && conversationDetails)) && <ConversationDetails />}

            {modal?.type === 'deleteConversation' && (
                <DeleteConversationModal
                    conversationId={modal.props?.conversationId}
                    conversationExistsOnLinkedIn={modal.props?.conversationExistsOnLinkedIn}
                    onClose={() => {}} // dispatch handled in modal
                />
            )}
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    width: 100%;
    flex: 1;
    min-height: 0;
    gap: 0;
`
const LeftPanel = styled.div<{ $width: number }>`
    width: ${({ $width }) => $width}px;
    flex-shrink: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border-right: 1px solid #dae0e8;
`

const ResizeHandle = styled.div`
    flex-shrink: 0;
    width: 6px;
    margin-left: -3px;
    cursor: col-resize;
    align-self: stretch;
    position: relative;
    z-index: 2;
    touch-action: none;

    &::after {
        content: '';
        position: absolute;
        left: 50%;
        top: 0;
        bottom: 0;
        width: 1px;
        transform: translateX(-50%);
        background: ${(p) => p.theme.quinaryColor || '#e5e5e5'};
    }

    &:hover::before {
        content: '';
        position: absolute;
        left: 50%;
        top: 0;
        bottom: 0;
        width: 3px;
        transform: translateX(-50%);
        background: ${(p) => p.theme.primaryColor || '#5964ff'};
        opacity: 0.35;
        border-radius: 2px;
    }
`
const SearchWrapper = styled.div`
    padding: 12px 12px 8px;
    flex-shrink: 0;
`
const MessagesPanel = styled.div`
    flex: 1;
    height: 100%;
    min-width: 0;
    min-height: 0;
    overflow: visible;
    display: flex;
    flex-direction: column;
    background: #fafafa;
`

export default Inbox
